import { useState, useEffect, useRef } from "react";
import { Download, Check, Maximize2, X } from "lucide-react";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";

interface DiagramProps {
  /**
   * Path to the SVG file (relative to public/)
   * Example: "/generated/diagrams/blog/auth-flow.svg"
   */
  src: string;

  /**
   * Alt text for accessibility
   */
  alt: string;

  /**
   * Optional caption displayed below the diagram
   */
  caption?: string;

  /**
   * Optional custom className for the container
   */
  className?: string;

  /**
   * Optional Mermaid source for semantic export (docs only).
   * When provided, renders a hidden code block in the DOM
   * that exporters (copy-as-markdown, llms-full.txt) can pick up.
   */
  mermaid?: string;
}

/**
 * Diagram component for rendering Mermaid-generated SVGs in MDX content.
 *
 * Usage in MDX:
 * ```mdx
 * <Diagram
 *   src="/generated/diagrams/blog/auth-flow.svg"
 *   alt="Authentication flow diagram"
 *   caption="How user authentication works"
 * />
 * ```
 *
 * For docs with semantic export:
 * ```mdx
 * <Diagram
 *   src="/generated/diagrams/docs/api-flow.svg"
 *   alt="API flow diagram"
 *   mermaid={`
 * flowchart LR
 *   A --> B
 * `}
 * />
 * ```
 */
export function Diagram({
  src,
  alt,
  caption,
  className = "",
  mermaid,
}: DiagramProps) {
  const [downloaded, setDownloaded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const viewLargerButtonRef = useRef<HTMLButtonElement>(null);

  // Get theme to show appropriate diagram variant
  const themeMode = usePreferencesStore((s) => s.themeMode);
  const themedSrc =
    themeMode === "light" ? src.replace(".svg", "-light.svg") : src;

  // Handle closing with animation
  const closeLightbox = () => {
    setIsClosing(true);
    setTimeout(() => {
      setLightboxOpen(false);
      setIsClosing(false);
    }, 150); // Match the CSS transition duration
  };

  // Escape key to close lightbox
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen]);

  // Prevent background scroll while lightbox is open
  useEffect(() => {
    if (!lightboxOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [lightboxOpen]);

  const handleViewLarger = () => {
    setLightboxOpen(true);
    // Blur the button to remove focus state
    viewLargerButtonRef.current?.blur();
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const filename = src.split("/").pop() || "diagram.svg";
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2000);
    } catch (err) {
      console.error("Failed to download diagram:", err);
    }
  };

  const mermaidText = mermaid?.trim();

  return (
    <>
      <figure className={`my-8 ${className}`}>
        {/* Diagram container with subtle border */}
        <div className="relative group rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
          {/* The diagram image - clickable to view larger */}
          <div
            className="p-4 sm:p-6 flex items-center justify-center cursor-zoom-in"
            onClick={handleViewLarger}
          >
            <img
              src={themedSrc}
              alt={alt}
              className="max-w-full h-auto"
              loading="lazy"
            />
          </div>

          {/* Action buttons - appear on hover */}
          <div className="absolute right-3 top-3 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* View larger button */}
            <button
              ref={viewLargerButtonRef}
              onClick={handleViewLarger}
              className="p-2 rounded-lg bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm border border-zinc-200 dark:border-zinc-700 text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-all hover:scale-105 focus:outline-none"
              aria-label="View larger diagram"
              title="View larger"
            >
              <Maximize2 className="h-4 w-4" />
            </button>

            {/* Download button */}
            <button
              onClick={handleDownload}
              className="p-2 rounded-lg bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm border border-zinc-200 dark:border-zinc-700 text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-all hover:scale-105 focus:outline-none"
              aria-label="Download SVG"
              title="Download SVG"
            >
              {downloaded ? (
                <Check className="h-4 w-4 text-green-500 dark:text-green-400" />
              ) : (
                <Download className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        {/* Optional caption */}
        {caption && (
          <figcaption className="mt-3 text-center text-sm text-zinc-500 dark:text-zinc-400">
            {caption}
          </figcaption>
        )}

        {/* Hidden Mermaid source for exporters (docs only) */}
        {mermaidText && (
          <pre data-diagram-source className="diagram-source-hidden">
            <code>{`~~~mermaid
${mermaidText}
~~~`}</code>
          </pre>
        )}
      </figure>

      {/* Lightbox overlay with fade transition */}
      {lightboxOpen && (
        <div
          role="dialog"
          className={`fixed inset-0 z-50 bg-black/90 flex items-center justify-center cursor-zoom-out transition-opacity duration-150 ${
            isClosing
              ? "opacity-0"
              : "opacity-100 animate-in fade-in duration-150"
          }`}
          onClick={closeLightbox}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors focus:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            aria-label="Close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={themedSrc}
            alt={alt}
            className={`max-w-[90vw] max-h-[90vh] object-contain cursor-default transition-all duration-150 ${
              isClosing ? "opacity-0 scale-95" : "opacity-100 scale-100"
            }`}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
